import { Alert, Button, Description, Drawer, FieldError, Input, Label, ListBox, NumberField, Select, Spinner } from "@heroui/react";
import type { Key } from "@heroui/react";
import { useEffect, useState } from "react";
import type { ReactNode, RefObject } from "react";
import type { PublicBranch } from "../../../../shared/types";
import { usePublicMenuTheme } from "../../../components/PublicMenuThemeProvider";
import { useToast } from "../../../contexts/ToastContext";
import { currentPosition, distanceMeters } from "../../../lib/geo";

interface SelectItem {
  id: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

function useDesktopDrawer(): boolean {
  const [desktop, setDesktop] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 768px)").matches,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia("(min-width: 768px)");
    const update = () => setDesktop(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return desktop;
}

export function ResponsiveDrawer({
  open,
  onOpenChange,
  title,
  children,
  footer,
  bodyRef,
  panel,
  wide = false,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  bodyRef?: RefObject<HTMLDivElement | null>;
  panel: "configurator" | "cart";
  wide?: boolean;
}) {
  const desktop = useDesktopDrawer();
  const { theme, portalContainer } = usePublicMenuTheme();
  const panelStyle =
    panel === "configurator"
      ? theme.appearance.configuratorStyle
      : theme.appearance.cartStyle;
  const panelClassName =
    panelStyle === "surfaceAlt" ? "menu-panel-alt" : "menu-panel";
  return (
    <Drawer.Backdrop
      UNSTABLE_portalContainer={portalContainer ?? undefined}
      isOpen={open}
      onOpenChange={onOpenChange}
      style={{ backdropFilter: "blur(4px)" }}
    >
      <Drawer.Content placement={desktop ? "right" : "bottom"}>
        <Drawer.Dialog
          className={`${desktop ? (wide ? "w-[min(94vw,760px)]" : "w-[min(94vw,620px)]") : "h-full w-full"} ${panelClassName}`}
        >
          {!desktop && <Drawer.Handle />}
          <Drawer.CloseTrigger aria-label="Cerrar panel" />
          <Drawer.Header
            className={`${panelClassName} border-b border-border px-5 py-4 sm:px-6`}
          >
            <Drawer.Heading className="menu-text-primary font-black tracking-tight">
              {title}
            </Drawer.Heading>
          </Drawer.Header>
          <Drawer.Body
            ref={bodyRef}
            className={`${panelClassName} px-4 py-5 sm:px-6`}
          >
            {children}
          </Drawer.Body>
          {footer && (
            <Drawer.Footer
              className={`${panelClassName} sticky bottom-0 z-10 border-t border-border px-4 py-4 shadow-surface sm:px-6`}
            >
              {footer}
            </Drawer.Footer>
          )}
        </Drawer.Dialog>
      </Drawer.Content>
    </Drawer.Backdrop>
  );
}

export function MenuSelect({
  label,
  value,
  items,
  placeholder,
  onChange,
  isRequired = false,
  isDisabled = false,
  description,
}: {
  label: string;
  value: string;
  items: SelectItem[];
  placeholder: string;
  onChange(value: string): void;
  isRequired?: boolean;
  isDisabled?: boolean;
  description?: string;
}) {
  const { portalContainer } = usePublicMenuTheme();
  return (
    <Select
      fullWidth
      isDisabled={isDisabled}
      isRequired={isRequired}
      disabledKeys={items
        .filter((item) => item.disabled)
        .map((item) => item.id)}
      placeholder={placeholder}
      value={value || null}
      variant="primary"
      onChange={(key: Key | null) => onChange(key === null ? "" : String(key))}
    >
      <Label>{label}</Label>
      <Select.Trigger>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      {description && <Description>{description}</Description>}
      <Select.Popover
        UNSTABLE_portalContainer={portalContainer ?? undefined}
        className="menu-panel border border-border shadow-lg"
      >
        <ListBox>
          {items.map((item) => (
            <ListBox.Item key={item.id} id={item.id} textValue={item.label}>
              <span className="flex min-w-0 flex-col">
                <span className="menu-text-primary font-semibold">
                  {item.label}
                </span>
                {item.description && (
                  <span className="menu-text-muted truncate text-xs">
                    {item.description}
                  </span>
                )}
              </span>
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
      <FieldError />
    </Select>
  );
}

export function QuantityField({
  label,
  value,
  onChange,
  min = 1,
  max = 20,
  compact = false,
}: {
  label: string;
  value: number;
  onChange(value: number): void;
  min?: number;
  max?: number;
  compact?: boolean;
}) {
  return (
    <NumberField
      aria-label={label}
      maxValue={max}
      minValue={min}
      value={value}
      variant="secondary"
      onChange={(next: number | undefined) => {
        if (next !== undefined)
          onChange(Math.min(max, Math.max(min, Math.round(next))));
      }}
    >
      <NumberField.Group className={compact ? "w-32" : "w-40"}>
        <NumberField.DecrementButton aria-label={`Reducir ${label}`} />
        <NumberField.Input className="min-w-0 text-center font-bold" />
        <NumberField.IncrementButton aria-label={`Aumentar ${label}`} />
      </NumberField.Group>
    </NumberField>
  );
}

export function BranchPicker({
  branches,
  selectedId,
  onSelect,
  compact = false,
}: {
  branches: PublicBranch[];
  selectedId: string;
  onSelect(id: string): void;
  compact?: boolean;
}) {
  const { show } = useToast();
  const [locating, setLocating] = useState(false);

  const suggest = async () => {
    setLocating(true);
    try {
      const position = await currentPosition();
      const point = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };
      const closest = [...branches].sort(
        (left, right) =>
          distanceMeters(point, left) - distanceMeters(point, right),
      )[0];
      if (closest) {
        onSelect(closest.id);
        show(`Sucursal sugerida: ${closest.name}.`, "success");
      }
    } catch (caught) {
      show(
        caught instanceof Error
          ? caught.message
          : "No se pudo obtener tu ubicación.",
        "error",
      );
    } finally {
      setLocating(false);
    }
  };

  return (
    <div
      className={`grid gap-2 ${compact ? "" : "sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end"}`}
    >
      <MenuSelect
        isRequired
        items={branches.map((branch) => ({
          id: branch.id,
          label: branch.name,
          description: branch.address,
        }))}
        label="Sucursal"
        placeholder="Selecciona una sucursal"
        value={selectedId}
        onChange={onSelect}
      />
      <Button
        isPending={locating}
        variant="primary"
        onPress={() => void suggest()}
      >
        {locating && <Spinner color="current" size="sm" />}
        Sugerir cercana
      </Button>
    </div>
  );
}

export function ConfigurationAlert({ issue }: { issue: string }) {
  return (
    <Alert status="danger">
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title>Falta completar la configuración</Alert.Title>
        <Alert.Description>{issue}</Alert.Description>
      </Alert.Content>
    </Alert>
  );
}

